import React, { useState, useEffect } from 'react';
import { Download, Smartphone, Gift, Sparkles, Check, ArrowRight, Zap, DollarSign, Clock, Lock } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import './BetaDownload.css';

const BetaDownload = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [passcode, setPasscode] = useState('');
  const [error, setError] = useState(false);
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);

  useEffect(() => {
    const auth = sessionStorage.getItem('betaAuth');
    if (auth === 'true') {
      setIsAuthenticated(true);
    }
  }, []);

  const handlePasscodeSubmit = (e) => {
    e.preventDefault();
    if (passcode === 'bHyyve2026') {
      setIsAuthenticated(true);
      sessionStorage.setItem('betaAuth', 'true');
      setError(false);
    } else {
      setError(true);
      setTimeout(() => setError(false), 3000);
    }
  };

  const handleNotify = (e) => {
    e.preventDefault();
    setSubscribed(true);
    setTimeout(() => setSubscribed(false), 5000);
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-black via-gray-900 to-black flex items-center justify-center px-4">
        <div className="max-w-md w-full">
          <div className="bg-gradient-to-br from-gray-900 to-black border-2 border-yellow-400 rounded-3xl p-8 shadow-2xl">
            <div className="text-center mb-8">
              <div className="bg-yellow-400/20 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
                <Lock className="text-yellow-400" size={40} />
              </div>
              <h1 className="text-3xl font-bold text-white mb-2">
                Exclusive Beta Access
              </h1>
              <p className="text-gray-400">
                Enter the passcode to continue
              </p>
            </div>

            <form onSubmit={handlePasscodeSubmit} className="space-y-6">
              <div>
                <Input
                  type="password"
                  value={passcode}
                  onChange={(e) => setPasscode(e.target.value)}
                  placeholder="Enter passcode"
                  className="w-full text-center text-2xl tracking-widest text-white placeholder-gray-500"
                  required
                />
              </div>

              {error && (
                <div className="bg-red-500/20 border-2 border-red-500 rounded-lg p-4 text-center">
                  <p className="text-red-500 font-semibold">Incorrect passcode. Please try again.</p>
                </div>
              )}

              <Button
                type="submit"
                className="w-full bg-yellow-400 hover:bg-yellow-500 text-black font-bold py-4 text-lg rounded-xl"
              >
                Access Beta Program
              </Button>
            </form>

            <div className="mt-8 text-center">
              <p className="text-gray-500 text-sm">
                Don't have access? Contact us for an invitation.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const benefits = [
    { icon: DollarSign, text: '$100 Sign-Up Bonus in your bHyyve Wallet' },
    { icon: Zap, text: 'Exclusive Beta Features & Early Access' },
    { icon: Gift, text: 'Priority Customer Support' },
    { icon: Sparkles, text: 'Special Promotions for Beta Testers' }
  ];

  const steps = [
    { number: '1', title: 'Download the App', description: 'Click the button below to get the bHyyve beta app' },
    { number: '2', title: 'Create Your Account', description: 'Sign up with your email and basic information' },
    { number: '3', title: 'Get Your Bonus', description: '$100 credited to your Wallet within 24-48 hours' }
  ];

  return (
    <div className="beta-download-page min-h-screen bg-gradient-to-b from-black via-gray-900 to-black">
      <section className="relative py-20 px-4 overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="floating-bee bee-1"></div>
          <div className="floating-bee bee-2"></div>
          <div className="floating-bee bee-3"></div>
        </div>

        <div className="container mx-auto relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-yellow-400/20 border-2 border-yellow-400 rounded-full px-6 py-2 mb-8 animate-pulse">
              <Sparkles className="text-yellow-400" size={20} />
              <span className="text-yellow-400 font-bold uppercase tracking-wider">Exclusive Beta Access</span>
              <Sparkles className="text-yellow-400" size={20} />
            </div>

            <h1 className="text-5xl md:text-7xl font-black text-white mb-6 leading-tight">
              Join the <span className="text-yellow-400">bHyyve</span> Beta
            </h1>
            
            <p className="text-xl md:text-2xl text-gray-300 mb-8 leading-relaxed">
              Be among the first to experience the future of service booking
            </p>

            <div className="bg-gradient-to-r from-yellow-400/20 via-yellow-400/30 to-yellow-400/20 border-4 border-yellow-400 rounded-3xl p-8 mb-12 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-yellow-400/10 rounded-full -translate-y-1/2 translate-x-1/2"></div>
              <div className="absolute bottom-0 left-0 w-24 h-24 bg-yellow-400/10 rounded-full translate-y-1/2 -translate-x-1/2"></div>
              
              <div className="relative z-10">
                <div className="flex items-center justify-center gap-3 mb-4">
                  <Gift className="text-yellow-400" size={48} />
                  <span className="text-6xl md:text-8xl font-black text-yellow-400">$100</span>
                </div>
                <h2 className="text-2xl md:text-3xl font-bold text-white mb-2">
                  Sign-Up Bonus
                </h2>
                <p className="text-gray-300 text-lg">
                  Added to your bHyyve Wallet within 24-48 hours
                </p>
              </div>
            </div>

            <a
              href="https://play.google.com/store/apps/details?id=com.customers.bhyyve"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block"
            >
              <Button className="bg-yellow-400 hover:bg-yellow-500 text-black font-bold py-8 px-12 text-xl rounded-2xl shadow-2xl hover:shadow-yellow-400/50 transition-all duration-300 hover:scale-105 group">
                <Download className="mr-3 group-hover:animate-bounce" size={28} />
                Download Beta App
                <ArrowRight className="ml-3 group-hover:translate-x-2 transition-transform" size={28} />
              </Button>
            </a>

            <p className="text-gray-400 text-sm mt-4">
              Available for Android devices only • Beta version
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 px-4 bg-black/50">
        <div className="container mx-auto">
          <h2 className="text-4xl font-bold text-center text-white mb-12">
            Why Join Our <span className="text-yellow-400">Beta Program?</span>
          </h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <div
                  key={index}
                  className="bg-gradient-to-br from-gray-900 to-black border-2 border-gray-800 hover:border-yellow-400 rounded-2xl p-6 text-center transition-all duration-300 hover:-translate-y-2 hover:shadow-xl hover:shadow-yellow-400/20"
                >
                  <div className="bg-yellow-400/20 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon className="text-yellow-400" size={32} />
                  </div>
                  <p className="text-white font-semibold leading-relaxed">
                    {benefit.text}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-16 px-4">
        <div className="container mx-auto max-w-5xl">
          <h2 className="text-4xl font-bold text-center text-white mb-12">
            How to Get <span className="text-yellow-400">Started</span>
          </h2>

          <div className="space-y-8">
            {steps.map((step, index) => (
              <div
                key={index}
                className="flex items-start gap-6 bg-gradient-to-r from-gray-900 to-black border-2 border-gray-800 rounded-2xl p-8 hover:border-yellow-400 transition-all duration-300"
              >
                <div className="flex-shrink-0 w-16 h-16 bg-yellow-400 rounded-full flex items-center justify-center text-black text-2xl font-black">
                  {step.number}
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-white mb-2">{step.title}</h3>
                  <p className="text-gray-300 text-lg">{step.description}</p>
                </div>
                <Check className="text-yellow-400 flex-shrink-0" size={32} />
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 px-4 bg-black/50">
        <div className="container mx-auto max-w-2xl text-center">
          <Smartphone className="mx-auto text-yellow-400 mb-6" size={64} />
          <h2 className="text-3xl font-bold text-white mb-4">
            Want Updates on New Features?
          </h2>
          <p className="text-gray-300 mb-8">
            Subscribe to get notified about app updates and exclusive beta features
          </p>

          {subscribed ? (
            <div className="bg-green-500/20 border-2 border-green-500 rounded-2xl p-6 flex items-center justify-center gap-3">
              <Check className="text-green-500" size={24} />
              <span className="text-green-500 font-bold text-lg">You're subscribed! Check your email.</span>
            </div>
          ) : (
            <form onSubmit={handleNotify} className="flex flex-col md:flex-row gap-4">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                required
                className="flex-1 bg-gray-900 border-2 border-gray-700 rounded-xl px-6 py-4 text-white placeholder-gray-500 focus:border-yellow-400 focus:outline-none"
              />
              <Button
                type="submit"
                className="bg-yellow-400 hover:bg-yellow-500 text-black font-bold px-8 py-4 rounded-xl"
              >
                Notify Me
              </Button>
            </form>
          )}
        </div>
      </section>

      <section className="py-16 px-4">
        <div className="container mx-auto max-w-4xl">
          <h2 className="text-3xl font-bold text-center text-white mb-12">
            Quick <span className="text-yellow-400">Questions</span>
          </h2>

          <div className="space-y-4">
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-6">
              <h3 className="text-xl font-bold text-white mb-2 flex items-center gap-2">
                <Clock className="text-yellow-400" size={24} />
                When will I receive my $100 bonus?
              </h3>
              <p className="text-gray-300">
                Your bonus will be credited to your bHyyve Wallet within 24-48 hours after account verification.
              </p>
            </div>

            <div className="bg-gray-900 border border-gray-800 rounded-xl p-6">
              <h3 className="text-xl font-bold text-white mb-2 flex items-center gap-2">
                <Smartphone className="text-yellow-400" size={24} />
                Is this available for iOS?
              </h3>
              <p className="text-gray-300">
                Currently, the beta is Android-only. iOS version coming soon! Subscribe above to be notified.
              </p>
            </div>

            <div className="bg-gray-900 border border-gray-800 rounded-xl p-6">
              <h3 className="text-xl font-bold text-white mb-2 flex items-center gap-2">
                <Gift className="text-yellow-400" size={24} />
                Can I use the bonus immediately?
              </h3>
              <p className="text-gray-300">
                Yes! Once credited, you can use your $100 bonus on any services within the bHyyve platform.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 px-4 bg-gradient-to-b from-black to-gray-900">
        <div className="container mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-black text-white mb-6">
            Ready to Start Buzzing?
          </h2>
          <p className="text-xl text-gray-300 mb-10">
            Join thousands of beta testers already enjoying bHyyve
          </p>
          <a
            href="https://play.google.com/store/apps/details?id=com.bhyyve"
            target="_blank"
            rel="noopener noreferrer"
          >
            <Button className="bg-yellow-400 hover:bg-yellow-500 text-black font-bold py-8 px-16 text-2xl rounded-2xl shadow-2xl hover:shadow-yellow-400/50 transition-all duration-300 hover:scale-105">
              Download Now
            </Button>
          </a>
          <p className="text-gray-500 text-sm mt-6">
            Limited beta slots available
          </p>
        </div>
      </section>
    </div>
  );
};

export default BetaDownload;
